import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock, Calendar, ArrowRight, Trash2 } from "lucide-react";
import { articlesApi } from "../lib/api";
import { toast } from "sonner";

const classificationConfig = {
  informativo: { label: "Informativo", color: "#3B82F6", bg: "rgba(59,130,246,0.1)" },
  practico: { label: "Práctico", color: "#10B981", bg: "rgba(16,185,129,0.1)" },
  aplicable: { label: "Aplicable", color: "#F0A050", bg: "rgba(240,160,80,0.1)" },
};

export default function ArticleCard({ article, index = 0, onDeleted }) {
  const navigate = useNavigate();
  const config = classificationConfig[article.classification] || classificationConfig.informativo;
  const formatDate = (d) => new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (e) => {
    e?.stopPropagation?.();
    setDeleting(true);
    try {
      await articlesApi.delete(article.id);
      toast.success("Artículo eliminado");
      setDeleteOpen(false);
      if (onDeleted) onDeleted(article.id);
    } catch {
      toast.error("Error al eliminar el artículo");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        data-testid={`article-card-${article.id}`}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/articles/${article.id}`)}
        onKeyDown={(e) => { if (e.key === "Enter") navigate(`/articles/${article.id}`); }}
        className={`w-full text-left group rounded-xl p-5 border transition-all duration-300 card-glow animate-fade-in-up animate-fade-in-up-delay-${Math.min(index + 1, 4)} relative cursor-pointer`}
        style={{ background: "#0F2035", borderColor: "#1E3A5F" }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,212,255,0.4)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1E3A5F"; e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <button
          type="button"
          data-testid={`delete-card-${article.id}`}
          onClick={(e) => { e.stopPropagation(); setDeleteOpen(true); }}
          aria-label="Eliminar artículo"
          className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}
        >
          <Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
        </button>
        <div className="flex items-start justify-between mb-3">
          <Badge className="text-xs px-2.5 py-0.5 rounded-full border"
            style={{ background: config.bg, color: config.color, borderColor: `${config.color}33` }}>{config.label}</Badge>
          <Badge variant="outline" className="text-xs px-2 py-0.5 rounded-full mr-8"
            style={{
              color: article.status === "published" ? "#4ADE80" : "#F0A050",
              borderColor: article.status === "published" ? "#4ADE8033" : "#F0A05033",
              background: article.status === "published" ? "rgba(74,222,128,0.08)" : "rgba(240,160,80,0.08)",
            }}>
            {article.status === "published" ? "Publicado" : "Borrador"}
          </Badge>
        </div>
        <h3 className="text-lg font-semibold mb-2 line-clamp-2 transition-colors duration-200"
          style={{ fontFamily: "'Crimson Pro', serif", color: "#E2E8F0" }}>{article.title}</h3>
        {article.meta_description && (
          <p className="text-sm mb-4 line-clamp-2" style={{ color: "#94A3B8", fontFamily: "'Outfit', sans-serif" }}>{article.meta_description}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1 text-xs" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>
              <Clock className="w-3.5 h-3.5" />{article.reading_time} min
            </span>
            <span className="flex items-center gap-1 text-xs" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>
              <Calendar className="w-3.5 h-3.5" />{formatDate(article.created_at)}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 transition-all duration-200 opacity-0 group-hover:opacity-100 group-hover:translate-x-1" style={{ color: "#00D4FF" }} />
        </div>
        {article.keywords?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3" style={{ borderTop: "1px solid #1E3A5F" }}>
            {article.keywords.slice(0, 3).map((kw, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded"
                style={{ background: "rgba(0,212,255,0.06)", color: "#64B5F6", fontFamily: "'IBM Plex Mono', monospace" }}>{kw}</span>
            ))}
          </div>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
          <DialogHeader>
            <DialogTitle style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk', serif" }}>Eliminar artículo</DialogTitle>
            <DialogDescription style={{ color: "#94A3B8" }}>
              ¿Seguro que quieres eliminar "<strong>{article.title}</strong>"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid={`cancel-delete-card-${article.id}`}
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}
            >
              Cancelar
            </Button>
            <Button
              data-testid={`confirm-delete-card-${article.id}`}
              onClick={handleDelete}
              disabled={deleting}
              style={{ background: "#ef4444", color: "white" }}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
